import type { CoachAvailability } from '@court-sync/shared';
import { minutesToTime, timeToMinutes } from '../../../utils/timeFormat';

/**
 * Removes busy intervals from free weekly windows (same day, minutes resolution).
 */
export function subtractBusyFromWeeklyWindows(
  freeWindows: { dayOfWeek: number; startTime: string; endTime: string }[],
  busyWindows: { dayOfWeek: number; startTime: string; endTime: string }[]
): { dayOfWeek: number; startTime: string; endTime: string }[] {
  const mergedBusy = mergeAdjacentAvailabilityWindows(busyWindows);
  const byDayBusy = new Map<number, { startTime: string; endTime: string }[]>();
  for (const b of mergedBusy) {
    const arr = byDayBusy.get(b.dayOfWeek) ?? [];
    arr.push({ startTime: b.startTime, endTime: b.endTime });
    byDayBusy.set(b.dayOfWeek, arr);
  }

  const out: { dayOfWeek: number; startTime: string; endTime: string }[] = [];

  for (const w of freeWindows) {
    const busyList = byDayBusy.get(w.dayOfWeek) ?? [];
    let pieces: { startTime: string; endTime: string }[] = [{ startTime: w.startTime, endTime: w.endTime }];
    for (const b of busyList) {
      const next: { startTime: string; endTime: string }[] = [];
      for (const p of pieces) {
        next.push(...subtractOneInterval(p, b));
      }
      pieces = next;
    }
    for (const p of pieces) {
      out.push({ dayOfWeek: w.dayOfWeek, startTime: p.startTime, endTime: p.endTime });
    }
  }

  return out.sort((a, b) =>
    a.dayOfWeek !== b.dayOfWeek
      ? a.dayOfWeek - b.dayOfWeek
      : timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  );
}

function subtractOneInterval(
  free: { startTime: string; endTime: string },
  busy: { startTime: string; endTime: string }
): { startTime: string; endTime: string }[] {
  const fs = timeToMinutes(free.startTime);
  const fe = timeToMinutes(free.endTime);
  const bs = timeToMinutes(busy.startTime);
  const be = timeToMinutes(busy.endTime);
  if (be <= fs || bs >= fe) {
    return [free];
  }
  const result: { startTime: string; endTime: string }[] = [];
  if (fs < bs) {
    result.push({ startTime: minutesToTime(fs), endTime: minutesToTime(Math.min(bs, fe)) });
  }
  if (be < fe) {
    result.push({ startTime: minutesToTime(Math.max(be, fs)), endTime: minutesToTime(fe) });
  }
  return result.filter((x) => timeToMinutes(x.startTime) < timeToMinutes(x.endTime));
}

export function privateSlotKey(slot: { dayOfWeek: number; startTime: string; endTime: string }): string {
  return `${slot.dayOfWeek}-${slot.startTime}-${slot.endTime}`;
}

/** Unique key for a specific calendar date + session time (day-by-day private booking). */
export function calendarPrivateSlotKey(sessionDateYmd: string, startTime: string, endTime: string): string {
  return `${sessionDateYmd}-${startTime}-${endTime}`;
}

/** True if the session slot lies entirely inside at least one weekly free window (after subtracting busy). */
export function isPrivateSlotWithinFreeWindows(
  slot: { dayOfWeek: number; startTime: string; endTime: string },
  freeWindows: { dayOfWeek: number; startTime: string; endTime: string }[]
): boolean {
  const ss = timeToMinutes(slot.startTime);
  const se = timeToMinutes(slot.endTime);
  if (se <= ss) return false;
  return freeWindows.some(
    (w) =>
      w.dayOfWeek === slot.dayOfWeek &&
      timeToMinutes(w.startTime) <= ss &&
      se <= timeToMinutes(w.endTime)
  );
}

export function availabilityWindowsFromCoachRows(rows: CoachAvailability[]): {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}[] {
  return rows
    .filter((r) => r.isAvailable)
    .map((r) => ({
      dayOfWeek: r.dayOfWeek,
      startTime: r.startTime.slice(0, 5),
      endTime: r.endTime.slice(0, 5),
    }));
}

/**
 * Coach availability is often stored as many short rows (e.g. 30-minute cells). Private booking
 * needs continuous windows so a session length (60+ min) can fit. Merge touching/overlapping
 * intervals on the same day.
 */
export function mergeAdjacentAvailabilityWindows(
  windows: { dayOfWeek: number; startTime: string; endTime: string }[]
): { dayOfWeek: number; startTime: string; endTime: string }[] {
  if (windows.length === 0) return [];

  const byDay = new Map<number, { startTime: string; endTime: string }[]>();
  for (const w of windows) {
    const arr = byDay.get(w.dayOfWeek) ?? [];
    arr.push({ startTime: w.startTime, endTime: w.endTime });
    byDay.set(w.dayOfWeek, arr);
  }

  const merged: { dayOfWeek: number; startTime: string; endTime: string }[] = [];

  for (const [day, intervals] of byDay) {
    const sorted = [...intervals].sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
    );
    let cur = { startTime: sorted[0].startTime, endTime: sorted[0].endTime };

    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      const curEnd = timeToMinutes(cur.endTime);
      const nextStart = timeToMinutes(next.startTime);
      if (nextStart <= curEnd) {
        cur.endTime = minutesToTime(Math.max(curEnd, timeToMinutes(next.endTime)));
      } else {
        merged.push({ dayOfWeek: day, startTime: cur.startTime, endTime: cur.endTime });
        cur = { startTime: next.startTime, endTime: next.endTime };
      }
    }
    merged.push({ dayOfWeek: day, startTime: cur.startTime, endTime: cur.endTime });
  }

  return merged.sort((a, b) =>
    a.dayOfWeek !== b.dayOfWeek
      ? a.dayOfWeek - b.dayOfWeek
      : timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  );
}

/**
 * When a coach has no weekly availability rows, the pick-times step still offers
 * Sun–Sat slots in this window (busy lessons still dim slots as usual).
 */
export function getDefaultWeeklyWindowsWhenCoachAvailabilityUnset(): {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}[] {
  return mergeAdjacentAvailabilityWindows(
    Array.from({ length: 7 }, (_, dayOfWeek) => ({
      dayOfWeek,
      startTime: '06:00',
      endTime: '22:00',
    }))
  );
}

/**
 * Builds every valid session window of length `durationMinutes` inside availability,
 * stepping `stepMinutes` between start times (e.g. 30 → 9:00, 9:30, 10:00…).
 */
export function buildSelectablePrivateSlots(
  windows: { dayOfWeek: number; startTime: string; endTime: string }[],
  durationMinutes: number,
  stepMinutes: number
): { dayOfWeek: number; startTime: string; endTime: string }[] {
  const out: { dayOfWeek: number; startTime: string; endTime: string }[] = [];
  const seen = new Set<string>();

  for (const w of windows) {
    let cur = timeToMinutes(w.startTime);
    const end = timeToMinutes(w.endTime);
    while (cur + durationMinutes <= end) {
      const endSlot = cur + durationMinutes;
      const startT = minutesToTime(cur);
      const endT = minutesToTime(endSlot);
      const row = { dayOfWeek: w.dayOfWeek, startTime: startT, endTime: endT };
      const k = privateSlotKey(row);
      if (!seen.has(k)) {
        seen.add(k);
        out.push(row);
      }
      cur += stepMinutes;
    }
  }

  return out.sort((a, b) =>
    a.dayOfWeek !== b.dayOfWeek
      ? a.dayOfWeek - b.dayOfWeek
      : timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  );
}
